import { useNavigate } from "react-router-dom";

import { useCurrentProjectId, useGates, useOverview } from "@/api/queries";
import type { Gate, OverviewProgress } from "@/api/types";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { Panel, SectionTitle } from "@/components/Panel";
import { ProgressRow, SkillRow } from "@/components/Meter";
import { RemoteState } from "@/components/RemoteState";
import { StatusLabel, type StatusTone } from "@/components/StatusLabel";
import { Tag } from "@/components/Tag";

/** 每个成长阶段对应一个真实关口，进度直接取关口状态。 */
const GROWTH_STEPS: { gate: string; title: string; detail: string }[] = [
  { gate: "G-1", title: "理解互动结构", detail: "六维分类通过 Profile 锁定" },
  { gate: "G2", title: "建立不可协商的真相", detail: "Canon 与状态字典锁定" },
  { gate: "G4", title: "写出有效选择", detail: "Storylet 与选择节点通过互动锁定" },
  { gate: "G5", title: "管理状态与回收", detail: "分场锁定并关闭 Canon 缺口" },
  { gate: "G7", title: "独立交付互动章节", detail: "通过六路径桌读与制片验收" },
];

/** 能力画像每一项都绑定一个真实产物类型的批准比例（来源字段见下）。 */
const SKILL_SOURCE: { label: string; type: string }[] = [
  { label: "人物与冲突", type: "canon" },
  { label: "有效选择", type: "choice" },
  { label: "状态设计", type: "state" },
  { label: "伏笔回收", type: "storylet" },
  { label: "制作控制", type: "scene" },
];

const GATE_TONE: Record<string, StatusTone> = {
  approved: "success",
  pending: "warning",
  active: "info",
  rejected: "danger",
  planned: "default",
};

const GATE_LABEL: Record<string, string> = {
  approved: "已完成",
  pending: "进行中",
  active: "进行中",
  rejected: "已驳回",
  planned: "未开始",
};

/**
 * 编剧成长。
 *
 * **不展示虚构的成长分数**：成长阶段直接取关口状态，能力画像取各产物类型的
 * 批准比例（来源字段 `overview.progress[].ratio`），待办取真实阻塞项与缺口。
 *
 * @dataSource mixed — 骨架与方法课程为固定内容，数值全部来自项目真实数据
 */
export function GrowthView() {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const overview = useOverview(projectId);
  const gates = useGates(projectId);

  const data = overview.data;
  const progress: OverviewProgress[] = data?.progress ?? [];
  const gateRows: Gate[] = gates.data ?? [];

  const ratioOf = (type: string): number => {
    const row = progress.find((item) => item.type === type);
    return row ? Math.round(row.ratio * 100) : 0;
  };

  const overall =
    progress.length === 0
      ? 0
      : Math.round((progress.reduce((sum, row) => sum + row.ratio, 0) / progress.length) * 100);

  const blockingIssues = (data?.issues ?? []).filter((issue) => issue.severity === "error");

  return (
    <div className="page-scroll">
      <div className="page-width">
        <RemoteState queries={[overview, gates]} title="成长视图加载失败">
          {data ? (
            <>
              <header className="page-heading">
                <div>
                  <span className="eyebrow">真实项目训练 · 不是泛课程</span>
                  <h2>让传统编剧在正式任务中获得互动叙事能力</h2>
                </div>
                <Tag tone={overall >= 80 ? "green" : "gold"}>当前项目完成度 {overall}%</Tag>
              </header>

              <div className="growth-layout">
                <Panel title="成长路径" description={`项目：${data.project.name}`}>
                  <div
                    style={{
                      margin: "-8px 0 14px",
                      fontFamily: "var(--font-display)",
                      fontSize: "22px",
                    }}
                  >
                    {overall}%
                  </div>
                  {GROWTH_STEPS.map((step, index) => {
                    const row = gateRows.find((gate) => gate.gate === step.gate);
                    const status = row?.status ?? "planned";
                    return (
                      <div
                        className={`path-step ${status === "pending" ? "is-current" : ""}`}
                        key={step.gate}
                      >
                        <span className="step-index">{index + 1}</span>
                        <div>
                          <strong>
                            {step.title} · {step.gate}
                          </strong>
                          <small>{step.detail}</small>
                        </div>
                        <StatusLabel tone={GATE_TONE[status] ?? "default"}>
                          {GATE_LABEL[status] ?? status}
                        </StatusLabel>
                      </div>
                    );
                  })}
                </Panel>

                <Panel
                  title="能力画像"
                  description="已批准产物占该类全部版本的比例"
                  bodyClassName="skill-grid"
                >
                  {SKILL_SOURCE.map((skill) => (
                    <SkillRow key={skill.label} label={skill.label} value={ratioOf(skill.type)} />
                  ))}
                </Panel>

                <Panel
                  title="当前待办"
                  description="真实阻塞项与未关闭缺口"
                  bodyClassName="method-list"
                >
                  {data.canon_gaps.length === 0 && blockingIssues.length === 0 ? (
                    <p className="inline-note">没有未关闭的缺口或阻塞问题。</p>
                  ) : (
                    <>
                      {data.canon_gaps.map((gap) => (
                        <article className="method-card" key={gap.id}>
                          <div className="meta-row">
                            <Tag tone="gold">Canon 缺口</Tag>
                            <Tag>{gap.id}</Tag>
                          </div>
                          <h3>{gap.topic}</h3>
                          <p>阻塞：{gap.blocks.join(" / ") || "—"}</p>
                          <div className="method-meta">
                            <span>状态：{gap.status}</span>
                            <Button
                              size="small"
                              variant="quiet"
                              onClick={() => navigate("/studio/canon")}
                            >
                              打开真相层
                            </Button>
                          </div>
                        </article>
                      ))}
                      {blockingIssues.map((issue, index) => (
                        <article className="method-card" key={`${issue.rule_id}-${index}`}>
                          <div className="meta-row">
                            <Tag tone="red">阻塞问题</Tag>
                            <Tag>{issue.rule_id}</Tag>
                          </div>
                          <h3>{issue.message}</h3>
                          <p>{issue.suggestion}</p>
                          <div className="method-meta">
                            <span>字段：{issue.field_path || "—"}</span>
                            <Button
                              size="small"
                              variant="quiet"
                              onClick={() => navigate("/studio/checks")}
                            >
                              前往检查
                            </Button>
                          </div>
                        </article>
                      ))}
                    </>
                  )}
                </Panel>

                <Panel title="最近一次审阅" description="关口与评审记录">
                  {data.activity.length === 0 ? (
                    <p className="inline-note">暂无审阅记录。</p>
                  ) : (
                    <>
                      <p
                        style={{
                          margin: 0,
                          fontFamily: "var(--font-display)",
                          fontSize: "17px",
                          lineHeight: 1.7,
                        }}
                      >
                        {data.activity[0].comment || "本次审批未留下意见。"}
                      </p>
                      <div className="meta-row" style={{ marginTop: 16 }}>
                        <Avatar role="writer">莫</Avatar>
                        <span
                          style={{
                            alignSelf: "center",
                            color: "var(--ink-soft)",
                            fontSize: "10px",
                          }}
                        >
                          {data.activity[0].gate} · {data.activity[0].decision} ·{" "}
                          {data.activity[0].actor}
                        </span>
                      </div>
                    </>
                  )}
                </Panel>
              </div>

              <SectionTitle title="产物推进度" description="按产物类型统计推进情况" />
              <div className="panel">
                <div className="panel-body progress-list">
                  {progress.length === 0 ? (
                    <p className="inline-note">尚无产物。</p>
                  ) : (
                    progress.map((row) => (
                      <ProgressRow
                        key={row.type}
                        label={row.label}
                        sublabel={`${row.approved}/${row.total} 已批准`}
                        value={Math.round(row.ratio * 100)}
                      />
                    ))
                  )}
                </div>
              </div>
            </>
          ) : null}
        </RemoteState>
      </div>
    </div>
  );
}
